import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getEnv } from 'config/Env';

import HelperFunctions from '../../pages/global/HelperFunctions';
import { baseQueryWithReauth, prepareHeaders, providesList } from '../api';

const baseUrl = getEnv('APP_API_URL');

const documentApiQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
});

export const documentApi = createApi({
    reducerPath: 'documentApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, documentApiQuery),
    tagTypes: [
        'Area',
        'AreaVariant',
        'Document',
        'DocumentVariant',
        'AreaHistory',
        'DocumentGroup',
        'Tag',
        'Block',
        'AreasBlockinfo',
        'SectionEdits',
        'TagGroup',
    ],
    endpoints: (builder) => ({
        getArea: builder.query({
            query: (id) => `/areas/${id}`,
            providesTags: (result, error, arg) => [
                { type: 'Area', id: arg },
                { type: 'Document', id: result.documentId },
            ],
        }),
        getBlock: builder.query({
            query: ({ blockId, variantId }) => `/blocks/${blockId}/variants/${variantId}`,
            providesTags: (result, error, { blockId }) => [{ type: 'Block', id: blockId }],
        }),
        deleteArea: builder.mutation({
            query: ({ id }) => ({
                url: `/areas/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { id, sectionId }) => [
                { type: 'Area', id },
                { type: 'Section', sectionId },
            ],
        }),
        getAreaBlocks: builder.query({
            query: ({ areaId, variantId }) => `/areas/${areaId}/variants/${variantId}/blocks`,
            keepUnusedDataFor: 0,
            providesTags: (result, error, arg) => [
                { type: 'Document', id: result.area.documentId },
                { type: 'AreaBlocks', id: arg.areaId },
                { type: 'Area', id: arg.areaId },
                {
                    type: 'AreaVariant',
                    id: `${arg.areaId}-${arg.variantId}`,
                },
            ],
        }),
        getAreaBaseBlocks: builder.query({
            query: ({ areaId, variantId }) => `/areas/${areaId}/variants/${variantId}/baseblocks`,
            keepUnusedDataFor: 0,
            providesTags: (result, error, arg) => [
                { type: 'AreaBaseBlocks', id: arg.areaId },
                {
                    type: 'AreaVariant',
                    id: `${arg.areaId}-${arg.variantId}`,
                },
            ],
        }),
        getBlockCloneContent: builder.query({
            query: (id) => `/blocks/${id}/clone`,
            providesTags: (result, error, arg) => [{ type: 'Block', id: arg }],
        }),
        getAreaHistory: builder.query({
            query: ({ areaId, variantId }) => `/areas/${areaId}/variants/${variantId}/groupedhistory`,
            providesTags: (result, error, arg) => [
                { type: 'Area', id: arg.areaId },
                { type: 'AreaHistory', id: arg.areaId },
                {
                    type: 'AreaVariant',
                    id: `${arg.areaId}-${arg.variantId}`,
                },
            ],
        }),
        getAreaHistoryBlocks: builder.query({
            query: ({ areaId, variantId, start, end }) =>
                `/areas/${areaId}/variants/${variantId}/groupedhistory/${start}/${end}/blocks`,
            providesTags: (result, error, arg) => [
                { type: 'Area', id: arg.areaId },
                { type: 'AreaHistory', id: arg.areaId },
                {
                    type: 'AreaVariant',
                    id: `${arg.areaId}-${arg.variantId}`,
                },
            ],
        }),
        updateAreaBlocks: builder.mutation({
            query: ({ areaId, variantId, body }) => ({
                url: `/areas/${areaId}/variants/${variantId}/blocks`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ areaId, variantId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        documentApi.util.updateQueryData('getAreaBlocks', { areaId, variantId }, (draft) => {
                            Object.assign(draft, data);
                        }),
                    );
                } catch {
                    console.error('Error in updateAreaBlocks');
                }
            },
        }),
        updateAreas: builder.mutation({
            query: ({ body }) => ({
                url: '/areas',
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { documentId }) => {
                const areaTags = result.map((area) => ({
                    type: 'Area',
                    id: area.id,
                }));

                return [...areaTags, { type: 'Document', id: documentId }];
            },
        }),
        updateArea: builder.mutation({
            query: ({ areaId, body }) => ({
                url: `/areas/${areaId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { areaId }) => [{ type: 'Area', id: areaId }],
        }),
        toggleAreaVariant: builder.mutation({
            query: ({ areaId, variantId }) => ({
                url: `/areas/${areaId}/variants/${variantId}/toggle`,
                method: 'PUT',
            }),
            invalidatesTags: (result, error, { areaId }) => [{ type: 'Area', id: areaId }],
        }),
        addArea: builder.mutation({
            query: ({ body }) => ({
                url: '/areas',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { documentId, sectionId }) => [
                {
                    type: 'Document',
                    id: documentId,
                },
                { type: 'Section', id: sectionId },
            ],
        }),
        updateAreaSortOrder: builder.mutation({
            query: ({ body }) => ({
                url: '/areas',
                method: 'PUT',
                body,
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                const { sectionId, documentId, body } = args;

                const patchResult = dispatch(
                    documentApi.util.updateQueryData('getAreas', { sectionId, documentId }, (draft) => {
                        const areas = draft.map((area) => {
                            const newArea = body.find((areaData) => areaData.id === area.id);
                            return {
                                ...area,
                                sortOrder: newArea?.sortOrder ?? area.sortOrder,
                            };
                        });
                        Object.assign(draft, areas);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (result, error, { sectionId, documentId }) => [
                { type: 'Section', id: sectionId },
                { type: 'Document', id: documentId },
                { type: 'Area', id: 'LIST' },
            ],
        }),
        updateSections: builder.mutation({
            query: ({ body }) => ({
                url: '/sections',
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { documentId }) => [{ type: 'Document', id: documentId }],
        }),
        syncSections: builder.mutation({
            query: ({ documentId, body }) => ({
                url: `/sections/${documentId}/syncs`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { documentId }) => [{ type: 'Document', id: documentId }],
        }),
        deleteSection: builder.mutation({
            query: ({ id, forceDelete }) => ({
                url: `/sections/${id}`,
                method: 'DELETE',
                params: {
                    forceDelete,
                },
            }),
            invalidatesTags: (result, error, { documentId }) => [{ type: 'Document', documentId }],
        }),
        addDocument: builder.mutation({
            query: ({ organisationId, body }) => ({
                url: `/organisations/${organisationId}/documents`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Document'],
        }),
        updateDocument: builder.mutation({
            query: ({ uri, body, method = 'PUT' }) => ({
                url: uri,
                method,
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Document', id },
                { type: 'Document', id: 'LIST' },
            ],
        }),
        updateBlocks: builder.mutation({
            query: ({ areaId, variantId, body }) => ({
                url: `/areas/${areaId}/variants/${variantId}/blocks`,
                method: 'PUT',
                body,
            }),
        }),
        resetBlocksChanges: builder.mutation({
            query: ({ areaId, variantId, body = {} }) => ({
                url: `/areas/${areaId}/variants/${variantId}/blocks/reset`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { areaId }) => [
                { type: 'Area', id: areaId },
                { type: 'AreaHistory', id: areaId },
                { type: 'AreaBaseBlocks', id: areaId },
                { type: 'AreaBlocks', id: areaId },
            ],
        }),
        processBlocksChanges: builder.mutation({
            query: ({ areaId, variantId, body = {} }) => ({
                url: `/areas/${areaId}/variants/${variantId}/blocks/changes`,
                method: 'PUT',
                body,
            }),
        }),
        updateDocuments: builder.mutation({
            query: ({ body }) => ({
                url: '/v2/documents/batches',
                method: 'POST',
                body,
            }),
            async onQueryStarted({ organisationId, body }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    documentApi.util.updateQueryData('getDocuments', { organisationId, deleted: false }, (draft) => {
                        draft.forEach((_document) => {
                            const documentPatch = body.find((patch) => patch.id === _document.id);

                            if (documentPatch) {
                                Object.assign(_document, {
                                    ..._document,
                                    ...documentPatch.changes,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: 'Document', id: 'LIST' }],
        }),
        deleteDocument: builder.mutation({
            query: (id) => ({
                url: `/documents/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Document', id: 'LIST' }],
        }),
        getDocument: builder.query({
            query: ({ id, showAreas = true }) => ({
                url: `/v2/documents/${id}`,
                params: {
                    showAreas,
                },
            }),
            providesTags: (result, error, { id }) => [{ type: 'Document', id }],
        }),
        getDocuments: builder.query({
            query: ({ organisationId, deleted = false }) =>
                deleted
                    ? `/organisations/${organisationId}/documents/deleted`
                    : `/organisations/${organisationId}/documents`,
            keepUnusedDataFor: 900, // keep data in memory for 15 minutes
            providesTags: (result) => providesList(result, 'Document', 'id'),
        }),
        getDocumentTags: builder.query({
            query: (id) => `/documents/${id}/tags`,
            providesTags: (result) => providesList(result, 'Tag', 'id'),
        }),
        getDocumentTagNames: builder.query({
            query: (id) => `/documents/${id}/tagnames`,
            providesTags: () => [{ type: 'Tag', id: 'LIST' }],
        }),
        getExportTags: builder.query({
            query: (id) => `/documents/${id}/tagexport`,
        }),
        getDocumentExport: builder.query({
            query: (id) => `/v2/documents/${id}/export`,
            providesTags: (result, error, { id }) => [{ type: 'Document', id }],
        }),
        getDocumentTag: builder.query({
            query: ({ documentId, id }) => `/documents/${documentId}/tags/${id}`,
            providesTags: (result, error, { id }) => [{ type: 'Tag', id }],
        }),
        getDocumentTagByName: builder.query({
            query: ({ documentId, name }) => ({
                url: `/documents/${documentId}/tags/by-name`,
                params: {
                    name,
                },
            }),
            providesTags: (result) => [{ type: 'Tag', id: result.id }],
        }),
        getDocumentTagSearch: builder.query({
            query: ({ documentId, params }) => ({
                url: `/documents/${documentId}/search/tags`,
                params,
            }),
        }),
        getOrganisationTagSearch: builder.query({
            query: ({ organisationId, params }) => ({
                url: `/organisations/${organisationId}/search/tags`,
                params,
            }),
        }),
        getOrganisationTags: builder.query({
            query: (id) => `/organisations/${id}/tags`,
            keepUnusedDataFor: 900, // keep data in memory for 15 minutes
            providesTags: (result) => providesList(result, 'Tag', 'id'),
        }),
        updateTag: builder.mutation({
            query: ({ organisationId, id, body }) => ({
                url: `/organisations/${organisationId}/tags/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Tag', id },
                { type: 'Tag', id: 'LIST' },
                { type: 'AreaBaseBlocks' },
            ],
        }),
        moveTag: builder.mutation({
            query: ({ id, body }) => ({
                url: `/tags/${id}/move`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Tag', id },
                { type: 'Tag', id: 'LIST' },
                { type: 'TagGroup', id: 'LIST' },
                { type: 'AreaBaseBlocks' },
            ],
            async onQueryStarted({ id, organisationId, body }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    documentApi.util.updateQueryData('getOrganisationTags', organisationId, (draft) => {
                        draft.forEach((_tag) => {
                            if (_tag.id === id) {
                                Object.assign(_tag, {
                                    ..._tag,
                                    ...body,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        addTag: builder.mutation({
            query: ({ organisationId, body }) => ({
                url: `/organisations/${organisationId}/tags`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Tag', id: 'LIST' }, { type: 'AreaBaseBlocks' }, { type: 'TagGroup' }],
        }),
        importTags: builder.mutation({
            query: ({ documentId, body }) => ({
                url: `/documents/${documentId}/tagimports`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'Tag' }, { type: 'TagGroup' }],
        }),
        syncTags: builder.mutation({
            query: ({ documentId, body }) => ({
                url: `/documents/${documentId}/tags/syncs`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Tag', id: 'LIST' }],
        }),
        deleteTag: builder.mutation({
            query: ({ organisationId, id }) => ({
                url: `/organisations/${organisationId}/tags/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Tag', id: 'LIST' }],
        }),
        getDocumentVariants: builder.query({
            query: (id) => `/documents/${id}/variants`,
            transformResponse: (response) => HelperFunctions.flatten([response.documentVariants]),
            providesTags: (result, error, arg) => [
                { type: 'Document', id: arg },
                { type: 'DocumentVariant', id: 'LIST' },
            ],
        }),
        getAreaRevisionOperationsVariantGroups: builder.query({
            query: ({ areaId, revisionId }) => `/areas/${areaId}/revisions/${revisionId}/operations/variant/groups`,
            transformResponse: (response) => response.groups,
        }),
        getDocumentsBlocksSearch: builder.query({
            query: ({ id, q }) => ({
                url: `/documents/${id}/blocks/search`,
                params: {
                    q,
                },
            }),
        }),
        updateDocumentVariants: builder.mutation({
            query: ({ id, body }) => ({
                url: `/documents/${id}/variants`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedVariant } = await queryFulfilled;

                    dispatch(
                        documentApi.util.updateQueryData('getDocumentVariants', id, (draft) => {
                            Object.assign(draft, HelperFunctions.flatten([updatedVariant.documentVariants]));
                        }),
                    );
                } catch {
                    console.error('updateDocumentVariants error');
                }
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'DocumentVariant', id }],
        }),
        updateLinkedAreas: builder.mutation({
            query: (body) => ({
                url: `/linked-areas/`,
                method: 'PUT',
                body,
            }),
        }),
        lockArea: builder.query({
            query: ({ areaId, body }) => ({
                url: `/areas/${areaId}/lock`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { areaId }) => [
                { type: 'AreaBlocks', id: areaId },
                { type: 'AreaBaseBlocks', id: areaId },
            ],
        }),
        unlockArea: builder.mutation({
            query: ({ areaId, force = false }) => ({
                url: `/areas/${areaId}/unlock`,
                method: 'PUT',
                body: {
                    force,
                },
            }),
            invalidatesTags: (result, error, { areaId, sectionId }) => [
                { type: 'AreasBlockinfo', id: sectionId },
                { type: 'SectionEdits' },
                { type: 'Area', id: areaId },
            ],
        }),
        updateAreaStatus: builder.mutation({
            query: ({ id, body }) => ({
                url: `/areas/${id}/status`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { id, sectionId }) => [
                { type: 'Area', id },
                { type: 'AreasBlockinfo', id: sectionId },
            ],
        }),
        getTableTemplates: builder.query({
            query: (id) => `/organisations/${id}/documents/tabletemplates`,
        }),
        getDocumentGroups: builder.query({
            query: () => `/documentgroups/groups`,
            keepUnusedDataFor: 3600, // keep data in memory for 1 hour
            providesTags: (result) => providesList(result, 'DocumentGroup', 'id'),
        }),
        addDocumentGroup: builder.mutation({
            query: (body) => ({
                url: '/documentgroups/groups',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['DocumentGroup'],
        }),
        updateDocumentGroup: builder.mutation({
            query: ({ id, body }) => ({
                url: `/documentgroups/${id}/group`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'DocumentGroup', id },
                { type: 'DocumentGroup', id: 'LIST' },
            ],
        }),
        updateDocumentGroups: builder.mutation({
            query: (body) => ({
                url: '/documentgroups/groups/batches',
                method: 'POST',
                body,
            }),
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    documentApi.util.updateQueryData('getDocumentGroups', undefined, (draft) => {
                        draft.forEach((_group) => {
                            const groupPatch = body.find((patch) => patch.id === _group.id);

                            if (groupPatch) {
                                Object.assign(_group, {
                                    ..._group,
                                    ...groupPatch.changes,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: 'DocumentGroup', id: 'LIST' }],
        }),
        deleteDocumentGroup: builder.mutation({
            query: (id) => ({
                url: `/documentgroups/${id}/group`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'DocumentGroup', id: 'LIST' }],
        }),
        deleteDocumentVariant: builder.mutation({
            query: ({ documentId, variantId }) => ({
                url: `/documents/${documentId}/variants/${variantId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { documentId }) => [
                { type: 'DocumentVariant', documentId },
                { type: 'Document', documentId },
            ],
        }),
        deleteBlock: builder.mutation({
            query: (id) => ({
                url: `/blocks/${id}`,
                method: 'DELETE',
            }),
        }),
        getAreas: builder.query({
            query: ({ sectionId }) => `/sections/${sectionId}/areas`,
            keepUnusedDataFor: 900, // keep data in memory for 15 minutes
            providesTags: (result, error, arg) => [
                ...providesList(result, 'Area', 'id'),
                {
                    type: 'Section',
                    id: arg.sectionId,
                },
                {
                    type: 'Document',
                    id: arg.documentId,
                },
            ],
        }),
        getAreasBlockinfo: builder.query({
            query: ({ sectionId }) => `/sections/${sectionId}/areas/blockinfo`,
            providesTags: (result, error, { documentId, sectionId }) => [
                {
                    type: 'AreasBlockinfo',
                    id: sectionId,
                },
                {
                    type: 'Document',
                    id: documentId,
                },
            ],
        }),
        batchAssignDocumentUsers: builder.mutation({
            query: (body) => ({
                url: '/v2/documents/batchassigns',
                method: 'POST',
                body,
            }),
        }),
        upsertDocumentTeam: builder.mutation({
            query: ({ id, body }) => ({
                url: `/v2/documents/${id}/teams/upserts`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'Document', id },
                { type: 'Document', id: 'LIST' },
            ],
        }),
        getDocumentSectionsEdits: builder.query({
            query: (documentId) => `/v2/documents/${documentId}/sections/edits`,
            providesTags: (result, error, id) => [
                ...providesList(result, 'Section', 'id'),
                {
                    type: 'Document',
                    id,
                },
                {
                    type: 'SectionEdits',
                    id,
                },
            ],
        }),
        getBlockAtTimestamp: builder.query({
            query: ({ blockId, timestamp }) => `/blocks/${blockId}/timestamps/${timestamp}`,
        }),
        getTagGroups: builder.query({
            query: (documentId) => (documentId ? `/tags/${documentId}/document/tags/groups` : `/tags/groups`),
            keepUnusedDataFor: 3600, // cache data 1 hour
            providesTags: (result) => providesList(result, 'TagGroup', 'id'),
        }),
        createTagGroup: builder.mutation({
            query: (body) => ({
                url: '/tags/groups',
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'TagGroup' }],
        }),
        updateTagGroup: builder.mutation({
            query: ({ id, body }) => ({
                url: `/tags/groups/${id}`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    documentApi.util.updateQueryData('getTagGroups', undefined, (draft) => {
                        draft.forEach((_group) => {
                            if (_group.id === id) {
                                Object.assign(_group, {
                                    ..._group,
                                    ...body,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'TagGroup', id }],
        }),
        updateTagsBulk: builder.mutation({
            query: ({ organisationId, body }) => ({
                url: `/organisations/${organisationId}/tagsbulk`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: () => [{ type: 'Tag' }, { type: 'TagGroup' }],
        }),
        deleteTagGroup: builder.mutation({
            query: (id) => ({
                url: `/tags/groups/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'TagGroup' }],
        }),
        getSectionExport: builder.query({
            query: ({ sectionId, variantId, timestamp }) =>
                `/sections/${sectionId}/variants/${variantId}/export?timestamp=${timestamp}`,
        }),
    }),
});

export const {
    useGetAreaQuery,
    useGetExportTagsQuery,
    useGetBlockQuery,
    useDeleteAreaMutation,
    useDeleteSectionMutation,
    useDeleteDocumentMutation,
    useGetAreaBlocksQuery,
    useGetAreaBaseBlocksQuery,
    useGetAreaHistoryQuery,
    useGetAreaHistoryBlocksQuery,
    useUpdateAreaBlocksMutation,
    useGetDocumentQuery,
    useGetDocumentsQuery,
    useGetDocumentGroupsQuery,
    useGetDocumentTagsQuery,
    useGetDocumentTagNamesQuery,
    useGetDocumentExportQuery,
    useGetDocumentTagQuery,
    useGetDocumentTagByNameQuery,
    useGetDocumentTagSearchQuery,
    useGetOrganisationTagsQuery,
    useUpdateTagMutation,
    useMoveTagMutation,
    useAddTagMutation,
    useImportTagsMutation,
    useSyncTagsMutation,
    useDeleteTagMutation,
    useGetDocumentVariantsQuery,
    useUpdateLinkedAreasMutation,
    useGetAreaRevisionOperationsVariantGroupsQuery,
    useGetDocumentsBlocksSearchQuery,
    useLockAreaQuery,
    useUnlockAreaMutation,
    useGetTableTemplatesQuery,
    useUpdateAreaMutation,
    useToggleAreaVariantMutation,
    useAddAreaMutation,
    useUpdateAreasMutation,
    useUpdateAreaSortOrderMutation,
    useUpdateSectionsMutation,
    useSyncSectionsMutation,
    useUpdateAreaStatusMutation,
    useUpdateDocumentMutation,
    useUpdateBlocksMutation,
    useResetBlocksChangesMutation,
    useProcessBlocksChangesMutation,
    useUpdateDocumentsMutation,
    useUpdateDocumentVariantsMutation,
    useAddDocumentMutation,
    useAddDocumentGroupMutation,
    useUpdateDocumentGroupMutation,
    useUpdateDocumentGroupsMutation,
    useDeleteDocumentGroupMutation,
    useDeleteDocumentVariantMutation,
    useDeleteBlockMutation,
    useGetBlockCloneContentQuery,
    useGetAreasQuery,
    useGetAreasBlockinfoQuery,
    useBatchAssignDocumentUsersMutation,
    useUpsertDocumentTeamMutation,
    useGetDocumentSectionsEditsQuery,
    useGetBlockAtTimestampQuery,
    useGetTagGroupsQuery,
    useCreateTagGroupMutation,
    useUpdateTagGroupMutation,
    useDeleteTagGroupMutation,
    useUpdateTagsBulkMutation,
    useGetSectionExportQuery,
} = documentApi;
